/**
 * Repository Query Sync
 * Cross-tab synchronization for repository cache using BroadcastChannel
 *
 * This enables repository data to stay synchronized across browser tabs:
 * - When one tab commits changes, other tabs see the update
 * - When one tab pulls data, other tabs get the cache invalidation
 */

import type { QueryClient } from '@tanstack/react-query';
import { dataSourceKeys } from '../../domains/data-binding/hooks/useDataSources';

const CHANNEL_NAME = 'creately-repository-sync';

type SyncMessageType = 'invalidate' | 'update' | 'commit' | 'pull';

interface SyncMessage {
  type: SyncMessageType;
  queryKey: readonly unknown[];
  timestamp: number;
  tabId: string;
}

/**
 * Generate a unique tab ID
 */
function generateTabId(): string {
  return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Class to manage cross-tab query synchronization
 */
export class RepositoryQuerySync {
  private channel: BroadcastChannel | null = null;
  private tabId: string;
  private queryClient: QueryClient | null = null;

  constructor() {
    this.tabId = generateTabId();
  }

  /**
   * Initialize the sync with a QueryClient
   */
  initialize(queryClient: QueryClient): void {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
      console.debug('[RepositoryQuerySync] BroadcastChannel not available');
      return;
    }

    this.queryClient = queryClient;

    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = this.handleMessage.bind(this);
      console.debug('[RepositoryQuerySync] Initialized for tab:', this.tabId);
    } catch (error) {
      console.error('[RepositoryQuerySync] Failed to create BroadcastChannel:', error);
    }
  }

  /**
   * Cleanup when no longer needed
   */
  cleanup(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.queryClient = null;
  }

  /**
   * Broadcast a query invalidation to other tabs
   */
  broadcastInvalidation(queryKey: readonly unknown[]): void {
    this.broadcast({
      type: 'invalidate',
      queryKey,
      timestamp: Date.now(),
      tabId: this.tabId,
    });
  }

  /**
   * Broadcast that a commit occurred
   */
  broadcastCommit(sourceId: string): void {
    this.broadcast({
      type: 'commit',
      queryKey: dataSourceKeys.records(sourceId),
      timestamp: Date.now(),
      tabId: this.tabId,
    });
  }

  /**
   * Broadcast that a pull occurred
   */
  broadcastPull(sourceId: string): void {
    this.broadcast({
      type: 'pull',
      queryKey: dataSourceKeys.records(sourceId),
      timestamp: Date.now(),
      tabId: this.tabId,
    });
  }

  /**
   * Send a message to other tabs
   */
  private broadcast(message: SyncMessage): void {
    if (!this.channel) {
      return;
    }

    try {
      this.channel.postMessage(message);
      console.debug('[RepositoryQuerySync] Broadcast:', message.type, message.queryKey);
    } catch (error) {
      console.error('[RepositoryQuerySync] Failed to broadcast:', error);
    }
  }

  /**
   * Handle incoming messages from other tabs
   */
  private handleMessage(event: MessageEvent<SyncMessage>): void {
    const message = event.data;

    // Ignore messages from this tab
    if (message.tabId === this.tabId) {
      return;
    }

    if (!this.queryClient) {
      return;
    }

    console.debug('[RepositoryQuerySync] Received:', message.type, 'from', message.tabId);

    switch (message.type) {
      case 'invalidate':
      case 'commit':
      case 'pull':
        // Invalidate the query to trigger a refetch
        this.queryClient.invalidateQueries({
          queryKey: message.queryKey as unknown[],
        });

        // Also invalidate the data sources list in case status changed
        this.queryClient.invalidateQueries({
          queryKey: dataSourceKeys.all,
        });
        break;

      default:
        console.warn('[RepositoryQuerySync] Unknown message type:', message.type);
    }
  }
}

// Singleton instance
let syncInstance: RepositoryQuerySync | null = null;

/**
 * Get or create the singleton sync instance
 */
export function getRepositoryQuerySync(): RepositoryQuerySync {
  if (!syncInstance) {
    syncInstance = new RepositoryQuerySync();
  }
  return syncInstance;
}

/**
 * Initialize repository query sync with a query client
 * Call this once when setting up the QueryClient
 */
export function initializeRepositorySync(queryClient: QueryClient): void {
  const sync = getRepositoryQuerySync();
  sync.initialize(queryClient);
}

/**
 * Cleanup repository query sync
 * Call this on app unmount
 */
export function cleanupRepositorySync(): void {
  if (syncInstance) {
    syncInstance.cleanup();
    syncInstance = null;
  }
}

/**
 * Helper to broadcast commit from anywhere in the app
 */
export function broadcastRepositoryCommit(sourceId: string): void {
  getRepositoryQuerySync().broadcastCommit(sourceId);
}

/**
 * Helper to broadcast pull from anywhere in the app
 */
export function broadcastRepositoryPull(sourceId: string): void {
  getRepositoryQuerySync().broadcastPull(sourceId);
}
